import React from 'react';
import { X, PlayCircle, PauseCircle, StopCircle, RotateCcw, Trophy, Plus, Minus, ArrowRight, ChevronRight } from 'lucide-react';

const MatchDetail = ({ 
  activeMatch, 
  nextMatch, 
  setView, 
  startMatch, 
  pauseMatch, 
  stopMatch, 
  resetMatchTime, 
  getMatchTime, 
  formatDuration, 
  formatTimeStr, 
  setWinner, 
  updateScore, 
  setActiveMatchId 
}) => (
  <div className="space-y-6 animate-in slide-in-from-right-4 duration-400 pb-12">
    <button onClick={() => setView('matches')} className="flex items-center gap-2 text-slate-400 font-bold mb-4 hover:text-emerald-500 transition-colors">
      <X className="w-5 h-5" /> Close Details
    </button>

    <div className="bg-slate-900 rounded-[2.5rem] border border-slate-800 overflow-hidden shadow-2xl">
      <div className="p-8 space-y-10">
        <div className="flex flex-col items-center gap-4">
          <div className="px-4 py-1.5 bg-slate-800 rounded-full border border-slate-700 text-[10px] font-black uppercase tracking-widest text-emerald-500">
            Arbitration Mode #{activeMatch.id}
          </div>
          
          {/* Timer with Seconds */}
          <div className="text-6xl font-black font-mono tracking-tighter text-white tabular-nums drop-shadow-[0_0_15px_rgba(255,255,255,0.1)]">
            {formatDuration(getMatchTime(activeMatch))}
          </div>

          {/* Start and End Times */}
          <div className="flex gap-8 text-[10px] font-black uppercase tracking-widest text-slate-500">
            {activeMatch.firstStartTime && (
              <div className="flex flex-col items-center">
                <span className="opacity-50 mb-1">Start</span>
                <span className="text-slate-300">{formatTimeStr(activeMatch.firstStartTime)}</span>
              </div>
            )}
            {activeMatch.endTime && (
              <div className="flex flex-col items-center">
                <span className="opacity-50 mb-1">End</span>
                <span className="text-rose-500">{formatTimeStr(activeMatch.endTime)}</span>
              </div>
            )}
          </div>

          <div className="flex flex-wrap justify-center gap-3 mt-4">
            {/* Play / Pause / Finish Button Logic */}
            {!activeMatch.isRunning ? (
              <button onClick={() => startMatch(activeMatch.id)} 
                className="flex items-center gap-2 bg-emerald-500 text-slate-950 px-6 py-4 rounded-2xl font-black uppercase text-xs shadow-lg shadow-emerald-500/20 active:scale-95 transition-all">
                <PlayCircle className="w-5 h-5" /> {activeMatch.firstStartTime ? 'Resume Game' : 'Initial Shot'}
              </button>
            ) : (
              <button onClick={() => pauseMatch(activeMatch.id)} 
                className="flex items-center gap-2 bg-amber-500 text-slate-950 px-6 py-4 rounded-2xl font-black uppercase text-xs shadow-lg shadow-amber-500/20 active:scale-95 transition-all">
                <PauseCircle className="w-5 h-5" /> Pause Match
              </button>
            )}

            {!activeMatch.endTime ? (
              <button onClick={() => stopMatch(activeMatch.id)} 
                className="flex items-center gap-2 bg-rose-500 text-slate-950 px-6 py-4 rounded-2xl font-black uppercase text-xs shadow-lg shadow-rose-500/20 active:scale-95 transition-all">
                <StopCircle className="w-5 h-5" /> End Game
              </button>
            ) : null}

            <button onClick={() => resetMatchTime(activeMatch.id)} 
              className="flex items-center gap-2 bg-slate-800 text-slate-400 px-6 py-4 rounded-2xl font-black uppercase text-xs border border-slate-700 active:scale-95 transition-all">
              <RotateCcw className="w-5 h-5" /> Reset Timer
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8">
          {/* Score Details */}
          <div className={`p-8 rounded-[2rem] transition-all border-2 ${activeMatch.winner === 1 ? 'bg-emerald-500/10 border-emerald-500/50' : 'bg-slate-950 border-slate-800'}`}>
            <div className="flex justify-between items-start mb-8">
              <div>
                <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest mb-2">North Duo</p>
                <h4 className="text-2xl font-black leading-tight uppercase tracking-tighter">{activeMatch.pair1[0]} <br/> {activeMatch.pair1[1]}</h4>
              </div>
              <button onClick={() => setWinner(activeMatch.id, 1)} className={`p-4 rounded-2xl transition-all ${activeMatch.winner === 1 ? 'bg-emerald-500 text-slate-950 scale-110' : 'bg-slate-800 text-slate-500'}`}>
                <Trophy className="w-7 h-7" />
              </button>
            </div>
            <div className="flex items-center justify-between gap-4">
              <button onClick={() => updateScore(activeMatch.id, 1, -1)} className="p-5 bg-slate-900 border border-slate-800 rounded-2xl active:bg-rose-500/20 transition-colors"><Minus className="w-8 h-8" /></button>
              <div className={`text-7xl font-black font-mono ${activeMatch.score1 < 0 ? 'text-rose-400' : 'text-emerald-400'}`}>{activeMatch.score1}</div>
              <button onClick={() => updateScore(activeMatch.id, 1, 1)} className="p-5 bg-slate-900 border border-slate-800 rounded-2xl active:bg-emerald-500/20 transition-colors"><Plus className="w-8 h-8" /></button>
            </div>
          </div>

          <div className={`p-8 rounded-[2rem] transition-all border-2 ${activeMatch.winner === 2 ? 'bg-emerald-500/10 border-emerald-500/50' : 'bg-slate-950 border-slate-800'}`}>
            <div className="flex justify-between items-start mb-8">
              <div className="text-right flex-1">
                <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest mb-2">South Duo</p>
                <h4 className="text-2xl font-black leading-tight uppercase tracking-tighter">{activeMatch.pair2[0]} <br/> {activeMatch.pair2[1]}</h4>
              </div>
              <button onClick={() => setWinner(activeMatch.id, 2)} className={`p-4 rounded-2xl transition-all ml-4 ${activeMatch.winner === 2 ? 'bg-emerald-500 text-slate-950 scale-110' : 'bg-slate-800 text-slate-500'}`}>
                <Trophy className="w-7 h-7" />
              </button>
            </div>
            <div className="flex items-center justify-between gap-4">
              <button onClick={() => updateScore(activeMatch.id, 2, -1)} className="p-5 bg-slate-900 border border-slate-800 rounded-2xl active:bg-rose-500/20 transition-colors"><Minus className="w-8 h-8" /></button>
              <div className={`text-7xl font-black font-mono ${activeMatch.score2 < 0 ? 'text-rose-400' : 'text-emerald-400'}`}>{activeMatch.score2}</div>
              <button onClick={() => updateScore(activeMatch.id, 2, 1)} className="p-5 bg-slate-900 border border-slate-800 rounded-2xl active:bg-emerald-500/20 transition-colors"><Plus className="w-8 h-8" /></button>
            </div>
          </div>
        </div>
      </div>
    </div>

    {nextMatch && (
      <div className="bg-slate-900/50 border border-slate-800 rounded-[2rem] p-6 mt-12 animate-in slide-in-from-bottom-4 delay-200">
          <div className="flex items-center gap-2 mb-4">
            <ArrowRight className="w-4 h-4 text-emerald-500" />
            <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-500">Next on the Table</h3>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-base font-black text-slate-400 space-y-1 uppercase tracking-tighter">
              <div>{nextMatch.pair1[0]} & {nextMatch.pair1[1]}</div>
              <div className="text-[10px] text-slate-600 font-black italic">vs</div>
              <div>{nextMatch.pair2[0]} & {nextMatch.pair2[1]}</div>
            </div>
            <button onClick={() => setActiveMatchId(nextMatch.id)} className="bg-emerald-500/10 text-emerald-400 p-4 rounded-2xl border border-emerald-500/20 active:scale-95 transition-all">
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
      </div>
    )}
  </div>
);

export default MatchDetail;
